// src/features/goal/services/indexedDB/objectiveRepository.ts
// Objective 專屬邏輯（本地優先架構）

import { Objective, ObjectiveStatus, CreateObjectiveRequest, DEFAULT_OBJECTIVE_DURATION_DAYS } from '../../types/objective';
import { STORE_NAMES, initDB } from '../../../../services/indexedDB/core/database';
import { SyncStatus, CleanupOptions, CleanupResult } from '../../../../services/indexedDB/core/types';
import { logger } from '../../../../utils/logger';

/** Firestore Timestamp 的鴨子型別（相容 Firebase SDK Timestamp 與序列化格式） */
export type FirestoreTimestampLike =
  | { toDate: () => Date }       // Firebase SDK Timestamp
  | { seconds: number; nanoseconds?: number };  // 序列化格式

/**
 * 本地優先的 Objective 類型
 * 包含同步狀態和版本管理
 */
export interface LocalObjective
  extends Omit<
    Objective,
    'startDate' | 'targetDate' | 'completedAt' | 'lastProgressAt' | 'createdAt' | 'updatedAt'
  > {
  // 日期欄位轉為 number（毫秒時間戳）
  startDate: number;
  targetDate?: number;
  completedAt?: number;
  lastProgressAt?: number;
  createdAt: number;
  updatedAt: number;

  // 本地快取元數據
  cachedAt: number; // 快取時間
  syncStatus: SyncStatus; // 同步狀態
  localVersion: number; // 本地版本號（用於衝突檢測）
  isLocalOnly?: boolean; // 是否僅存在於本地（尚未同步到伺服器）
}

/**
 * Objective Repository
 * 處理 Objective 相關的 IndexedDB 操作（本地優先架構）
 *
 * 注意：不繼承 BaseRepository，因為 Objective 使用 LocalObjective 類型
 * 有自己的日期轉換和同步狀態邏輯
 */
class ObjectiveRepository {
  protected readonly storeName = STORE_NAMES.OBJECTIVES;
  protected readonly defaultRetentionDays = 30;

  /**
   * 將 Objective 轉換為本地儲存格式
   */
  toLocal(objective: Objective, syncStatus: SyncStatus = 'synced'): LocalObjective {
    const now = Date.now();

    // 安全地轉換 Timestamp 到 number
    const toTimestamp = (value: number | Date | FirestoreTimestampLike | null | undefined): number => {
      if (!value) return now;
      if (typeof value === 'number') return value;
      if (value instanceof Date) return value.getTime();
      if ('toDate' in value) return value.toDate().getTime(); // Firebase SDK Timestamp
      if ('seconds' in value) return value.seconds * 1000;   // 序列化格式
      return now;
    };

    const toOptionalTimestamp = (value: number | Date | FirestoreTimestampLike | null | undefined): number | undefined => {
      if (!value) return undefined;
      return toTimestamp(value);
    };

    return {
      ...objective,
      startDate: toTimestamp(objective.startDate),
      targetDate: toOptionalTimestamp(objective.targetDate),
      completedAt: toOptionalTimestamp(objective.completedAt),
      lastProgressAt: toOptionalTimestamp(objective.lastProgressAt),
      createdAt: toTimestamp(objective.createdAt),
      updatedAt: toTimestamp(objective.updatedAt),
      cachedAt: now,
      syncStatus,
      localVersion: 1,
      isLocalOnly: false,
    };
  }

  /**
   * 將本地格式轉換回 Objective（用於 UI 顯示）
   *
   * 注意：日期欄位保持為 number（毫秒時間戳），與 Firebase Timestamp 兼容。
   * UI 層可以根據需要使用 new Date(timestamp) 轉換。
   * 這是有意設計，因為：
   * 1. number 和 Timestamp 在大多數操作中兼容
   * 2. 避免不必要的轉換開銷
   */
  fromLocal(local: LocalObjective): Objective {
    const { cachedAt, syncStatus, localVersion, isLocalOnly, ...objective } = local;
    // 型別斷言：日期欄位為 number，與 Timestamp 運行時兼容
    return objective as unknown as Objective;
  }

  /**
   * 批量保存 Objectives（使用本地格式）
   * @param _userId - 用戶 ID（保留參數以維持 API 一致性，實際使用 objective.userId）
   */
  async saveManyWithSync(
    _userId: string,
    objectives: Objective[],
    syncStatus: SyncStatus = 'synced'
  ): Promise<void> {
    if (!objectives.length) return;

    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      for (const objective of objectives) {
        const localObjective = this.toLocal(objective, syncStatus);
        store.put(localObjective);
      }

      return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      logger.error('Failed to save objectives', error);
      throw error;
    }
  }

  /**
   * 獲取所有 Objectives（本地格式）
   */
  async getAllLocal(
    userId: string,
    options?: {
      status?: ObjectiveStatus | ObjectiveStatus[];
      excludeArchived?: boolean;
    }
  ): Promise<LocalObjective[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId');

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId);

        request.onsuccess = () => {
          let objectives = request.result as LocalObjective[];

          // 過濾狀態
          if (options?.status) {
            const statusArray = Array.isArray(options.status) ? options.status : [options.status];
            objectives = objectives.filter((obj) => statusArray.includes(obj.status));
          }

          // 排除 archived
          if (options?.excludeArchived) {
            objectives = objectives.filter((obj) => obj.status !== 'archived');
          }

          // 按 updatedAt 降序排列
          objectives.sort((a, b) => b.updatedAt - a.updatedAt);

          resolve(objectives);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Failed to get all objectives', error);
      throw error;
    }
  }

  /**
   * 獲取單個 Objective（本地格式）
   */
  async getLocalById(id: string): Promise<LocalObjective | null> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Failed to get objective by ID', error);
      throw error;
    }
  }

  /**
   * 獲取待同步的 Objectives
   */
  async getPending(userId: string): Promise<LocalObjective[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_syncStatus');

      return new Promise((resolve, reject) => {
        const request = index.getAll([userId, 'pending']);
        request.onsuccess = () => resolve(request.result as LocalObjective[]);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Failed to get pending objectives', error);
      throw error;
    }
  }

  /**
   * 更新同步狀態
   * @param id - 本地 ID（可能是臨時 ID）
   * @param syncStatus - 新的同步狀態
   * @param serverId - 可選的伺服器 ID（同步成功後替換臨時 ID）
   */
  async updateSyncStatus(id: string, syncStatus: SyncStatus, serverId?: string): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const getRequest = store.get(id);

        getRequest.onsuccess = () => {
          if (getRequest.result) {
            const objective = getRequest.result;
            objective.syncStatus = syncStatus;
            objective.cachedAt = Date.now();

            // 如果同步成功且有伺服器 ID，更新相關欄位
            if (syncStatus === 'synced' && serverId) {
              objective.isLocalOnly = false;
              // 如果是臨時 ID，需要替換
              if (id !== serverId && id.startsWith('temp_')) {
                objective.id = serverId;
                // 刪除舊的臨時記錄，添加新的伺服器 ID 記錄
                store.delete(id);
              }
            }

            const putRequest = store.put(objective);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            reject(new Error(`Objective with ID ${id} not found`));
          }
        };

        getRequest.onerror = () => reject(getRequest.error);
      });
    } catch (error) {
      logger.error('Failed to update objective sync status', error);
      throw error;
    }
  }

  /**
   * 獲取活躍的 Objectives 數量
   */
  async getActiveCount(userId: string): Promise<number> {
    try {
      const objectives = await this.getAllLocal(userId, {
        excludeArchived: true,
      });

      // 計算活躍的 Objectives（狀態為 active）
      const activeCount = objectives.filter((obj) => obj.status === 'active').length;

      return activeCount;
    } catch (error) {
      logger.error('Failed to get active objectives count', error);
      throw error;
    }
  }

  /**
   * 從創建請求生成完整的 LocalObjective
   * @param userId - 用戶 ID
   * @param request - 創建請求（部分欄位）
   * @returns 完整的 LocalObjective
   */
  private buildLocalObjective(userId: string, request: CreateObjectiveRequest): LocalObjective {
    const now = Date.now();
    const targetDate = request.targetDate
      ? request.targetDate.getTime()
      : now + DEFAULT_OBJECTIVE_DURATION_DAYS * 24 * 60 * 60 * 1000;

    return {
      id: `temp_${now}_${Math.random().toString(36).substring(2, 9)}`,
      userId,
      title: request.title,
      category: request.category,
      status: request.status || 'active',
      linkedObjectiveId: request.linkedObjectiveId,
      totalRipples: 0,
      weeklyRipples: [],
      currentWeek: 1,
      startDate: now,
      targetDate,
      linkedTasksCount: 0,
      createdAt: now,
      updatedAt: now,
      cachedAt: now,
      syncStatus: 'pending',
      localVersion: 1,
      isLocalOnly: true,
    };
  }

  /**
   * 從創建請求創建本地 Objective（標記為待同步）
   * @param userId - 用戶 ID
   * @param request - 創建請求
   * @returns 創建的 LocalObjective 物件
   */
  async createFromRequest(userId: string, request: CreateObjectiveRequest): Promise<LocalObjective> {
    try {
      const local = this.buildLocalObjective(userId, request);

      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const putRequest = store.put(local);
        putRequest.onsuccess = () => resolve(local);
        putRequest.onerror = () => reject(putRequest.error);
      });
    } catch (error) {
      logger.error('Failed to create local objective from request', error);
      throw error;
    }
  }

  /**
   * 創建本地 Objective（標記為待同步）
   * @deprecated 使用 createFromRequest 替代
   * @returns 創建的 LocalObjective 物件
   */
  async createLocal(objective: Objective): Promise<LocalObjective> {
    try {
      const local = this.toLocal(objective, 'pending');
      local.isLocalOnly = true;

      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.put(local);
        request.onsuccess = () => resolve(local);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Failed to create local objective', error);
      throw error;
    }
  }

  /**
   * 更新本地 Objective（標記為待同步）
   */
  async updateLocal(id: string, updates: Partial<Objective>): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const getRequest = store.get(id);

        getRequest.onsuccess = () => {
          if (getRequest.result) {
            const existing = getRequest.result;

            // 合併更新
            const updated = {
              ...existing,
              ...updates,
              cachedAt: Date.now(),
              updatedAt: Date.now(),
              syncStatus: 'pending' as SyncStatus, // 標記為待同步
              localVersion: existing.localVersion + 1,
            };

            const putRequest = store.put(updated);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            reject(new Error(`Objective with ID ${id} not found`));
          }
        };

        getRequest.onerror = () => reject(getRequest.error);
      });
    } catch (error) {
      logger.error('Failed to update local objective', error);
      throw error;
    }
  }

  /**
   * 刪除本地 Objective
   */
  async deleteLocal(id: string): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => {
          logger.info('Objective deleted from IndexedDB', { id });
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Failed to delete objective locally', error);
      throw error;
    }
  }

  /**
   * 清理過期的 Objectives
   */
  async cleanupExpired(userId: string, options?: CleanupOptions): Promise<CleanupResult> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId');

      const retentionDays = options?.retentionDays ?? this.defaultRetentionDays;
      const cutoffDate = options?.beforeDate ?? new Date(Date.now() - retentionDays * 24 * 60 * 60 * 1000);
      const cutoffTime = cutoffDate.getTime();

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId);

        request.onsuccess = () => {
          const allEntities = request.result as LocalObjective[];
          let deletedCount = 0;
          const scannedCount = allEntities.length;

          for (const entity of allEntities) {
            // 只清理已歸檔且過期的 Objectives
            if (entity.status === 'archived' && entity.cachedAt && entity.cachedAt < cutoffTime) {
              store.delete(entity.id);
              deletedCount++;
            }
          }

          tx.oncomplete = () => {
            logger.info('Objectives cleanup completed', {
              deletedCount,
              remainingCount: scannedCount - deletedCount,
              scannedCount,
            });

            resolve({
              deletedCount,
              remainingCount: scannedCount - deletedCount,
              scannedCount,
            });
          };

          tx.onerror = () => reject(tx.error);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logger.error('Failed to cleanup objectives', error);
      throw error;
    }
  }
}

// 導出單例
export const objectiveRepository = new ObjectiveRepository();
